import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { formatIsoDay, formatTRY } from "@/lib/format";
import { Stat, sel, todayIso, useAccounts } from "@/lib/finance";
import { useAuth } from "@/context/AuthContext";
import SupplierPaymentDialog from "@/components/finance/SupplierPaymentDialog";

const ISS_CLS = { verildi: "bg-blue-50 text-blue-700 border-blue-200", odendi: "bg-emerald-50 text-emerald-700 border-emerald-200", iade: "bg-amber-50 text-amber-800 border-amber-200" };

function IssuedRow({ i, accounts, onChanged }) {
  const { can } = useAuth();
  const [acc, setAcc] = useState(i.account_id || "");
  const ok = accounts.filter((a) => a.type === "banka" || (i.kind === "senet" && a.type === "kasa"));
  const go = async (status) => {
    if (status === "iade" && !window.confirm(`${i.no} iade alındı olarak işaretlensin mi? Tedarikçi borcu geri yazılır.`)) return;
    try { await api.post(`/supplier-payments/issued/${i.id}/transition`, { status, account_id: status === "odendi" ? acc || ok[0]?.id : null, date: todayIso() }); toast.success(`${i.no} güncellendi`); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <tr data-testid={`issued-row-${i.no}`}>
      <td className="font-mono text-xs">{i.no}</td><td>{i.kind_label}</td><td className="font-mono text-xs">{i.serial_no}</td><td className="max-w-[200px] truncate">{i.supplier_name}</td>
      <td className={`font-mono text-xs ${i.is_overdue ? "text-rose-600 font-semibold" : ""}`}>{formatIsoDay(i.due_date)}</td><td className="text-right font-mono">{formatTRY(i.amount)}</td>
      <td><span className={`text-[11px] px-1.5 py-0.5 rounded border ${ISS_CLS[i.status]}`} data-testid={`issued-status-${i.no}`}>{i.status_label}</span></td>
      <td className="text-right whitespace-nowrap">{can("finans", "edit") && i.status === "verildi" && <>
        <select className={`${sel} h-8 text-xs mr-1`} value={acc} onChange={(e) => setAcc(e.target.value)} data-testid={`issued-account-${i.no}`}>{ok.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
        <Button size="sm" variant="outline" className="h-8" onClick={() => go("odendi")} data-testid={`issued-pay-${i.no}`}>Ödendi</Button>
        <Button size="sm" variant="ghost" className="h-8 text-amber-700" onClick={() => go("iade")} data-testid={`issued-return-${i.no}`}>İade</Button></>}</td>
    </tr>
  );
}

export default function SupplierPaymentsPanel({ reload, onChanged }) {
  const { can } = useAuth();
  const accounts = useAccounts();
  const [pays, setPays] = useState(null);
  const [iss, setIss] = useState(null);
  const [n, setN] = useState(0);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    api.get("/supplier-payments").then(({ data }) => setPays(data));
    api.get("/supplier-payments/issued").then(({ data }) => setIss(data));
  }, [n, reload]);
  const bump = () => { setN(n + 1); onChanged?.(); };
  return (
    <div className="space-y-3" data-testid="supplier-payments-panel">
      <div className="flex items-center gap-3">
        <div className="grid grid-cols-2 gap-3 flex-1 max-w-xl">
          <Stat label="Verilen Açık Çek/Senet" value={formatTRY(iss?.open_total)} hint={`${iss?.open_count || 0} evrak · vadesinde nakit akışına çıkış`} tone="text-rose-600" testid="issued-open-total" />
          <Stat label="Tedarikçi Ödemeleri" value={pays?.total ?? "—"} hint="Kayıt sayısı" testid="supplier-payments-count" />
        </div>
        {can("finans", "create") && <Button className="ml-auto bg-blue-600 hover:bg-blue-700" onClick={() => setAdding(true)} data-testid="new-supplier-payment-button"><Plus className="h-4 w-4 mr-1" />Tedarikçi Ödemesi</Button>}
      </div>
      <div className="panel overflow-x-auto"><div className="px-3 py-2 border-b text-[12px] font-semibold">Verilen Çek / Senet</div>
        <table className="erp-table text-[13px]" data-testid="issued-table"><thead><tr><th>No</th><th>Tür</th><th>Seri</th><th>Tedarikçi</th><th>Vade</th><th className="text-right">Tutar</th><th>Durum</th><th></th></tr></thead>
          <tbody>{!iss ? null : iss.items.length === 0 ? <tr><td colSpan={8} className="text-slate-400">Verilen evrak yok</td></tr> : iss.items.map((i) => <IssuedRow key={i.id} i={i} accounts={accounts} onChanged={bump} />)}</tbody></table>
      </div>
      <div className="panel overflow-x-auto"><div className="px-3 py-2 border-b text-[12px] font-semibold">Tedarikçi Ödemeleri</div>
        <table className="erp-table text-[13px]" data-testid="supplier-payments-table"><thead><tr><th>No</th><th>Tarih</th><th>Tedarikçi</th><th>Şekil</th><th>Hesap / Evrak</th><th className="text-right">Tutar</th><th>Kaydeden</th></tr></thead>
          <tbody>{!pays ? null : pays.items.length === 0 ? <tr><td colSpan={7} className="text-slate-400">Ödeme yok</td></tr> : pays.items.map((p) => (
            <tr key={p.id} data-testid={`supplier-payment-row-${p.payment_no}`}><td className="font-mono text-xs">{p.payment_no}</td><td className="font-mono text-xs">{formatIsoDay(p.date)}</td><td className="max-w-[220px] truncate">{p.supplier_name}</td>
              <td>{p.method_label}</td><td className="text-xs">{p.issued_no || p.account_name || "—"}</td><td className="text-right font-mono font-semibold">{formatTRY(p.amount)}</td><td className="text-xs">{p.created_by_name}</td></tr>))}</tbody></table>
      </div>
      {adding && <SupplierPaymentDialog onClose={() => setAdding(false)} onDone={() => { setAdding(false); bump(); }} />}
    </div>
  );
}
